import React, { useEffect, useMemo, useState } from 'react';
import { Box, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material';
import { AutoStories, Close } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { font } from '../../utils/fontHelper';
import { useUserWatchlistData } from '../../contexts/UserWatchlistDataContext';
import { useUserMovieData } from '../../contexts/UserMovieDataContext';
import { useUserFollow } from '../../contexts/UserFollowContext';
import { useCinemaTheme } from '../../contexts/CinemaThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { hasEverPostedStory } from '../../services/storiesService';
import { scrollFadeMaskSx } from '../../utils/scrollStyles';
import {
  HomeStackCard,
  listActiveHomeStackCards,
} from '../../services/homeStackCardsService';
import { buildDefaultCards, type CardEntry } from './defaultHomeCards';

const ONBOARDING_KEY = 'home_card_stack_dismissed_onboarding';
const FEATURE_KEY = 'home_card_stack_dismissed_feature';
const RESET_EVENT = 'home-card-stack:reset';

// Module-level caches survive remounts within a session, preventing flicker
// when the user switches tabs and HomeCardStack remounts.
let _adminCards: HomeStackCard[] | null = null;
let _adminCardsFetching = false;
const _hasPostedStory = new Map<string, boolean>();

export function resetHomeCardStackDismissals(): void {
  try {
    localStorage.removeItem(ONBOARDING_KEY);
    localStorage.removeItem(FEATURE_KEY);
  } catch { /* noop */ }
  window.dispatchEvent(new Event(RESET_EVENT));
}

// Was true during the registry refactor so cards stayed visible while iterating.
// Now flipped back — cards gate on `isCompleted()` + persisted dismissals.
const DEV_ALWAYS_SHOW_ON_HOME = false;

function loadDismissed(key: string): Set<string> {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr : []);
  } catch { return new Set(); }
}

function persistDismissed(key: string, set: Set<string>): void {
  try { localStorage.setItem(key, JSON.stringify(Array.from(set))); } catch { /* noop */ }
}

interface HomeCardStackProps {
  alwaysShow?: boolean;
  forceList?: boolean;
  forceGrid?: boolean;
  hideDismiss?: boolean;
}

const HomeCardStack: React.FC<HomeCardStackProps> = ({ alwaysShow = false, forceList = false, forceGrid = false, hideDismiss = false }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { user } = useAuth();
  const { userWatchlists, likedWatchlists, isLoading: watchlistLoading, likedWatchlistsReady } = useUserWatchlistData();
  const { getWatchlistMovieIds, getWatchedMovieIds, getReviewedMovieIds, isLoading: movieLoading } = useUserMovieData();
  const { followStats, isLoading: followLoading } = useUserFollow();
  const { cinemaTheme, hasChosenTheme } = useCinemaTheme();

  const wantToWatchCount = getWatchlistMovieIds().length;
  const watchedCount = getWatchedMovieIds().length;
  const reviewedCount = getReviewedMovieIds().length;
  const likedListCount = likedWatchlists.length;
  const followedCount = followStats.followedUsers.length;

  const [adminCards, setAdminCards] = useState<HomeStackCard[]>(() => _adminCards ?? []);
  useEffect(() => {
    if (_adminCards !== null || _adminCardsFetching) return;
    _adminCardsFetching = true;
    let cancelled = false;
    (async () => {
      try {
        const fetched = await listActiveHomeStackCards();
        _adminCards = fetched;
        if (!cancelled) setAdminCards(fetched);
      } catch { /* admin cards are optional */ } finally {
        _adminCardsFetching = false;
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const [dismissedOnboarding, setDismissedOnboarding] = useState<Set<string>>(() => loadDismissed(ONBOARDING_KEY));
  const [dismissedFeature, setDismissedFeature] = useState<Set<string>>(() => loadDismissed(FEATURE_KEY));

  useEffect(() => {
    const onReset = () => {
      setDismissedOnboarding(loadDismissed(ONBOARDING_KEY));
      setDismissedFeature(loadDismissed(FEATURE_KEY));
    };
    window.addEventListener(RESET_EVENT, onReset);
    return () => window.removeEventListener(RESET_EVENT, onReset);
  }, []);

  const [hasPostedStory, setHasPostedStory] = useState<boolean>(() =>
    user?.uid ? (_hasPostedStory.get(user.uid) ?? true) : true,
  );
  // Tracks whether the story-check async fetch has settled so we don't flash
  // cards that will immediately disappear once we know the real value.
  const [storyResolved, setStoryResolved] = useState<boolean>(() =>
    !user?.uid || _hasPostedStory.has(user.uid),
  );
  useEffect(() => {
    if (!user?.uid) { setStoryResolved(true); return; }
    if (_hasPostedStory.has(user.uid)) { setStoryResolved(true); return; }
    let cancelled = false;
    (async () => {
      try {
        const posted = await hasEverPostedStory(user.uid);
        _hasPostedStory.set(user.uid, posted);
        if (!cancelled) { setHasPostedStory(posted); setStoryResolved(true); }
      } catch {
        if (!cancelled) setStoryResolved(true); // show with safe default on error
      }
    })();
    return () => { cancelled = true; };
  }, [user?.uid]);

  const cards: CardEntry[] = useMemo(() => {
    const defaults = buildDefaultCards({
      wantToWatchCount,
      watchedCount,
      reviewedCount,
      watchlistCount: userWatchlists.length,
      likedListCount,
      followedCount,
      hasPostedStory,
      cinemaTheme,
      hasChosenTheme,
      navigate,
      emit: (evt) => window.dispatchEvent(new Event(evt)),
    });

    const fromAdmin: CardEntry[] = adminCards.map((c) => ({
      id: `admin:${c.id}`,
      kind: 'feature',
      icon:
        c.iconType === 'image' && c.iconImageUrl ? (
          <Box component="img" src={c.iconImageUrl} alt="" sx={{ width: 28, height: 28, objectFit: 'cover', borderRadius: '50%' }} />
        ) : c.iconType === 'svg' && c.iconSvg ? (
          <Box sx={{ width: 24, height: 24, display: 'flex', '& svg': { width: '100%', height: '100%' } }} dangerouslySetInnerHTML={{ __html: c.iconSvg }} />
        ) : (
          <AutoStories sx={{ fontSize: 20 }} />
        ),
      title: c.title,
      description: c.subtitle,
      ctaLabel: '',
      onAction: () => navigate(c.navigationPath),
      isCompleted: () => false,
    }));

    return [...defaults, ...fromAdmin];
  }, [
    userWatchlists.length, wantToWatchCount, watchedCount, reviewedCount,
    likedListCount, followedCount, hasPostedStory, cinemaTheme, hasChosenTheme,
    adminCards, navigate,
  ]);

  // Wait for all completion-gating data before rendering — prevents cards
  // from flashing in and then immediately hiding once counts are known.
  const dataReady = !watchlistLoading && !movieLoading && !followLoading && storyResolved && likedWatchlistsReady;

  const effectiveAlwaysShow = alwaysShow || DEV_ALWAYS_SHOW_ON_HOME;
  const visibleCards = cards.filter((c) => {
    if (effectiveAlwaysShow) return true;
    if (c.isCompleted()) return false;
    const dismissed = c.kind === 'onboarding' ? dismissedOnboarding : dismissedFeature;
    return !dismissed.has(c.id);
  });

  // Stability gate. isLoading=false from each context arrives before the
  // backing counts/values actually settle (localStorage cache returns first,
  // Firebase fetch arrives later). Without this gate, the stack renders
  // briefly with stale counts that mark cards incomplete, then re-renders with
  // accurate counts that mark them complete — and disappears. We require the
  // data signature to be stable for one paint before committing to render.
  const dataSignature = [
    wantToWatchCount,
    watchedCount,
    reviewedCount,
    userWatchlists.length,
    likedListCount,
    followedCount,
    hasPostedStory ? 1 : 0,
    cinemaTheme,
    hasChosenTheme ? 1 : 0,
    adminCards.length,
  ].join('|');
  const [stableSignature, setStableSignature] = useState<string | null>(null);
  useEffect(() => {
    if (!dataReady) {
      setStableSignature(null);
      return;
    }
    // ~1 frame of stability is enough for batched context updates to land
    // without being long enough for the user to notice the delay.
    const id = window.setTimeout(() => setStableSignature(dataSignature), 80);
    return () => window.clearTimeout(id);
  }, [dataReady, dataSignature]);

  if (!dataReady || stableSignature !== dataSignature || visibleCards.length === 0) return null;

  const dismiss = (card: CardEntry) => {
    if (card.kind === 'onboarding') {
      setDismissedOnboarding((prev) => { const next = new Set(prev); next.add(card.id); persistDismissed(ONBOARDING_KEY, next); return next; });
    } else {
      setDismissedFeature((prev) => { const next = new Set(prev); next.add(card.id); persistDismissed(FEATURE_KEY, next); return next; });
    }
  };

  const mobileGridThreshold = 2;
  const desktopGridThreshold = 4;
  const gridThreshold = isMobile ? mobileGridThreshold : desktopGridThreshold;
  const useGrid = visibleCards.length <= gridThreshold;

  const card = (entry: CardEntry, scrollSnap: boolean, noBottomMargin = false) => (
    <Box
      key={entry.id}
      onClick={entry.onAction}
      sx={{
        position: 'relative',
        ...(scrollSnap
          ? { flex: '0 0 auto', scrollSnapAlign: 'start', width: { xs: 'max(70vw, 200px)', sm: 'max(50vw, 300px)' }, minWidth: 200 }
          : { width: '100%', minWidth: 0 }),
        borderRadius: 2.5,
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
        bgcolor: isDark ? 'rgba(255,255,255,0.035)' : 'rgba(0,0,0,0.02)',
        px: 1.5,
        py: 1.25,
        mb: noBottomMargin ? 0 : 3,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.25,
        transition: 'transform 0.18s cubic-bezier(0.4,0,0.2,1), background-color 0.18s ease',
        '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.035)' },
        '&:active': { transform: 'scale(0.985)' },
      }}
    >
      <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.palette.text.primary, flexShrink: 0, mt: 0.25 }}>
        {entry.icon}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <Typography sx={{ fontFamily: font(), fontWeight: 600, fontSize: 13.5, color: theme.palette.text.primary, lineHeight: 1.25, pr: hideDismiss ? 0 : 2.5, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
          {entry.title}
        </Typography>
        <Typography sx={{ fontFamily: font(), fontSize: 12, color: theme.palette.text.secondary, lineHeight: 1.3, mt: 0.25, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
          {entry.description}
        </Typography>
      </Box>

      {!hideDismiss && (
        <IconButton
          size="small"
          aria-label="Dismiss"
          onClick={(e: React.MouseEvent) => { e.stopPropagation(); dismiss(entry); }}
          sx={{ position: 'absolute', top: 2, right: 2, padding: 0.5, color: theme.palette.text.secondary, '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)' } }}
        >
          <Close sx={{ fontSize: 14 }} />
        </IconButton>
      )}
    </Box>
  );

  if (forceGrid) {
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'stretch', gap: 1.25 }}>
        {visibleCards.map((c) => <Box key={c.id} sx={{ flex: '1 1 200px', minWidth: 200, display: 'flex' }}>{card(c, false, true)}</Box>)}
      </Box>
    );
  }

  if (forceList) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        {visibleCards.map((c) => card(c, false, true))}
      </Box>
    );
  }

  if (useGrid) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 1.25, overflowX: 'auto', scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
        {visibleCards.map((c) => <Box key={c.id} sx={{ flex: '0 0 max(calc(50% - 5px), 200px)', minWidth: 200, display: 'flex' }}>{card(c, false)}</Box>)}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 1.25, overflowX: 'auto', scrollSnapType: 'x mandatory', pb: 1, ...scrollFadeMaskSx, scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
      {visibleCards.map((c) => card(c, true))}
    </Box>
  );
};

export default HomeCardStack;
